import ResizeObserver from 'resize-observer-polyfill';
import { useRef, useState } from 'react';
import { useVariable } from './useVariable';
import { useNodeRef } from './useNodeRef';
import useLayoutEffect from './useIsomorphicLayoutEffect';

// References:
// https://github.com/cvpcasada/use-measure/blob/master/src/use-measure.ts
// https://github.com/souporserious/react-measure/blob/main/src/with-content-rect.js
const DEFAULT_BOUNDS = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0
};
const areBoundsEqual = (b1, b2) => !b1 && !b2 || b1 && b2 && b1.x === b2.x && b1.y === b2.y && b1.width === b2.width && b1.height === b2.height && b1.top === b2.top && b1.right === b2.right && b1.bottom === b2.bottom && b1.left === b2.left;
export const useMeasure = (onResize, initialRef) => {
  const [bounds, setBounds] = useState(DEFAULT_BOUNDS);
  const boundsRef = useRef(DEFAULT_BOUNDS);
  const observerRef = useRef(null);
  const measuredNodeRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const handleResize = useVariable(onResize);
  const measure = useVariable(([entry]) => {
    animationFrameIdRef.current = window.requestAnimationFrame(() => {
      if (!areBoundsEqual(boundsRef.current, entry.contentRect)) {
        boundsRef.current = entry.contentRect;
        setBounds(entry.contentRect);
        handleResize.current?.(entry.contentRect);
      }
    });
  });
  useLayoutEffect(() => {
    observerRef.current = new ResizeObserver((...args) => measure.current(...args));
    if (measuredNodeRef.current) {
      observerRef.current.observe(measuredNodeRef.current);
    }
    return () => {
      window.cancelAnimationFrame(animationFrameIdRef.current);
      observerRef.current.disconnect();
    };
  }, []);
  const [setRef, nodeRef] = useNodeRef(initialRef, node => {
    if (observerRef.current) {
      if (measuredNodeRef.current) {
        observerRef.current.unobserve(measuredNodeRef.current);
      }
      observerRef.current.observe(node);
    }
    measuredNodeRef.current = node;
  });
  return {
    nodeRef,
    setRef,
    bounds
  };
};